import React from "react";

/* Every route is code-split, so each one is a separate hash-named chunk file.
   A deploy ships only the new build, which means a tab still running the
   previous one asks for chunks the server no longer has. nginx answers those
   with index.html instead of a 404, the browser cannot parse HTML as
   JavaScript, and webpack raises a ChunkLoadError.

   Reloading fixes it: the fresh index.html carries the new chunk names. The
   flag below stops that turning into a reload loop when the chunk is missing
   for some other reason — it is cleared as soon as any chunk loads, so it can
   only ever suppress a second consecutive failure. Session storage, not local:
   the guard belongs to this tab and should not outlive it. */
const RELOADED_KEY = "chunk-reload-attempted";

/* Webpack names the error when the chunk never registers itself. When the
   server answered with HTML the script fails to parse first, so match the
   wording of that too rather than relying on the name alone. */
export const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Unexpected token/i.test(
    error?.message || "",
  );

/* Storage throws outright when cookies are blocked, so neither read nor write
   is allowed to be the thing that breaks the page. */
const reloadAlreadyTried = () => {
  try {
    return window.sessionStorage.getItem(RELOADED_KEY) === "true";
  } catch {
    return false;
  }
};

const rememberReload = (tried) => {
  try {
    if (tried) {
      window.sessionStorage.setItem(RELOADED_KEY, "true");
    } else {
      window.sessionStorage.removeItem(RELOADED_KEY);
    }
  } catch {
    /* Nothing to fall back to; the boundary still catches a repeat failure. */
  }
};

/* Drop-in for React.lazy that recovers from a chunk left behind by a deploy. */
const lazyWithRetry = (factory) =>
  React.lazy(async () => {
    try {
      const loaded = await factory();
      // A chunk arrived, so whatever went wrong before is behind us.
      rememberReload(false);
      return loaded;
    } catch (error) {
      if (!isChunkLoadError(error) || reloadAlreadyTried()) {
        // Either not a stale chunk, or reloading has already been tried and
        // did not help. Hand it to the error boundary.
        throw error;
      }
      rememberReload(true);
      window.location.reload();
      /* Deliberately never settles: Suspense holds its loader until the reload
         takes over, so no error screen flashes on the way out. */
      return new Promise(() => {});
    }
  });

export default lazyWithRetry;
